import { hostedShowsLayerChildren, matchHostedComponent } from '#react/hosted-components'

import type { SceneGraph, SceneNode } from '@open-pencil/scene-graph'

export interface LayerSelectionMode {
  additive: boolean
  range: boolean
}

export function layerChildren(graph: SceneGraph, id: string): SceneNode[] {
  const parent = graph.getNode(id)
  if (parent && matchHostedComponent(parent, graph) && !hostedShowsLayerChildren(graph, parent)) {
    return []
  }
  return graph.getChildren(id).filter((child) => !child.internalOnly)
}

export interface VisibleLayerRow {
  id: string
  depth: number
  hasChildren: boolean
}

/**
 * Flatten only expanded branches — collapsed subtrees stay unloaded in the
 * visible list (lazy expand).
 */
export function collectVisibleLayerRows(
  graph: SceneGraph,
  parentId: string,
  expandedIds: ReadonlySet<string>
): VisibleLayerRow[] {
  const rows: VisibleLayerRow[] = []
  function walk(nodes: ReturnType<typeof layerChildren>, depth: number) {
    for (const child of nodes) {
      const children = layerChildren(graph, child.id)
      const hasChildren = children.length > 0
      rows.push({ id: child.id, depth, hasChildren })
      if (hasChildren && expandedIds.has(child.id)) walk(children, depth + 1)
    }
  }
  walk(layerChildren(graph, parentId), 0)
  return rows
}

export function collectVisibleLayerIds(
  graph: SceneGraph,
  parentId: string,
  expandedIds: ReadonlySet<string>
): string[] {
  return collectVisibleLayerRows(graph, parentId, expandedIds).map((row) => row.id)
}

export function ancestorIdsToExpand(
  graph: SceneGraph,
  selectedIds: Iterable<string>,
  pageId: string
): string[] {
  const ancestors: string[] = []
  const seen = new Set<string>()
  for (const id of selectedIds) {
    let node = graph.getNode(id)
    while (node?.parentId && node.parentId !== pageId) {
      const parentId = node.parentId
      if (!seen.has(parentId)) {
        seen.add(parentId)
        ancestors.push(parentId)
      }
      node = graph.getNode(parentId)
    }
  }
  return ancestors
}

export function layerSelectionModeFromEvent(event: {
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
}): LayerSelectionMode {
  return {
    additive: event.metaKey || event.ctrlKey,
    range: event.shiftKey
  }
}

export function layerSelectionForTarget(
  visibleIds: readonly string[],
  currentIds: ReadonlySet<string>,
  anchorId: string | null,
  targetId: string,
  mode: LayerSelectionMode
): Set<string> {
  if (!mode.range || !anchorId) {
    if (!mode.additive) return new Set([targetId])
    const next = new Set(currentIds)
    if (next.has(targetId)) next.delete(targetId)
    else next.add(targetId)
    return next
  }

  const anchorIndex = visibleIds.indexOf(anchorId)
  const targetIndex = visibleIds.indexOf(targetId)
  if (anchorIndex === -1 || targetIndex === -1) return new Set([targetId])
  const start = Math.min(anchorIndex, targetIndex)
  const end = Math.max(anchorIndex, targetIndex)
  const next = mode.additive ? new Set(currentIds) : new Set<string>()
  for (let index = start; index <= end; index++) {
    const id = visibleIds[index]
    if (id) next.add(id)
  }
  return next
}
